import React from "react";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Skeleton from "@material-ui/lab/Skeleton";

import { makeStyles } from "@material-ui/core/styles";
import { green, red } from "@material-ui/core/colors";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import moment from "moment";

import Rating from "@material-ui/lab/Rating";
import { i18n } from "../../../translate/i18n";

/**
 * Attendant interface — represents a support agent/attendant
 */
interface Attendant {
  name: string;
  rating: number | null;
  avgSupportTime: number;
  online: boolean;
}

/**
 * RatingBox props interface
 */
interface RatingBoxProps {
  rating: number | null;
}

/**
 * TableAttendantsStatus props interface
 */
interface TableAttendantsStatusProps {
  loading: boolean;
  attendants: Attendant[];
}

const useStyles = makeStyles((theme) => ({
  on: {
    color: green[600],
    fontSize: "20px",
  },
  off: {
    color: red[600],
    fontSize: "20px",
  },
  pointer: {
    cursor: "pointer",
  },
}));

/**
 * RatingBox component — renders a Material-UI Rating widget
 * @param {RatingBoxProps} props — contains rating value (0-3)
 * @returns {React.ReactElement} Rating component
 */
export function RatingBox({ rating }: RatingBoxProps): React.ReactElement {
  const ratingTrunc = rating === null ? 0 : Math.trunc(rating);
  return <Rating defaultValue={ratingTrunc} max={3} readOnly />;
}

/**
 * TableAttendantsStatus component — displays table of attendants status and ratings
 * @param {TableAttendantsStatusProps} props — loading and attendants list
 * @returns {React.ReactElement} Table or Skeleton loading state
 */
const TableAttendantsStatus: React.FC<TableAttendantsStatusProps> = ({
  loading,
  attendants,
}): React.ReactElement => {
  const classes = useStyles();

  function renderList(): React.ReactElement[] {
    return attendants.map((a, k) => (
      <TableRow key={k}>
        <TableCell>{a.name}</TableCell>
        <TableCell
          align="center"
          title={i18n.t("dashboard.onlineTable.ratingLabel")}
          className={classes.pointer}
        >
          <RatingBox rating={a.rating} />
        </TableCell>
        <TableCell align="center">{formatTime(a.avgSupportTime)}</TableCell>
        <TableCell align="center">
          {a.online ? (
            <CheckCircleIcon className={classes.on} />
          ) : (
            <ErrorIcon className={classes.off} />
          )}
        </TableCell>
      </TableRow>
    ));
  }

  function formatTime(minutes: number): string {
    return moment()
      .startOf("day")
      .add(minutes, "minutes")
      .format("HH[h] mm[m]");
  }

  return !loading ? (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t("dashboard.onlineTable.name")}</TableCell>
            <TableCell align="center">
              {i18n.t("dashboard.onlineTable.ratings")}
            </TableCell>
            <TableCell align="center">
              {i18n.t("dashboard.onlineTable.avgSupportTime")}
            </TableCell>
            <TableCell align="center">
              {i18n.t("dashboard.onlineTable.status")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderList()}</TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Skeleton variant="rect" height={150} />
  );
};

export default TableAttendantsStatus;
